import { Component, Injector, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { take } from 'rxjs/operators';
import { SvgIconService } from '../../../../../components/common/svg-icon/svg-icon.service';
import { WidgetBaseComponent } from '../../widgetBase';

/**
 * Esg Widget
 */
@Component({
  selector: 'esg-widget',
  templateUrl: './esg-widget.component.html',
  styleUrls: ['../../common/css/general.scss', './esg-widget.component.scss'],
  encapsulation: ViewEncapsulation.None //None because of specific kendo css
})
export class EsgWidgetComponent extends WidgetBaseComponent implements OnInit {

  /**
   * Widget Guid
   */
  @Input() widgetGuid: string;

  /**
   * Heat Hot Water Percent
   */
  heatHotWaterPercent = 0;

  /**
   * Electricity Percent
   */
  electricityPercent = 0;

  /**
   * Demand Certificate Primary Energy Percent
   */
  demandCertificatePrimaryEnergyPercent = 0;

  /**
   * Slider options
   */
  sliderOptions = {
    min: 0,
    max: 100,
    tickPlacement: 'none',
    showButtons: false
  };

  /**
  * Constructor
  */
  constructor(injector: Injector, private svgIconService: SvgIconService) {
    super(injector, 'esg', false);
  }

  /**
   * Initializes the component
   */
  ngOnInit() {
    // will call registerEvents and onInitialized when config (this.widgetConfig and this.page) is ready

    this.initWidgetConfig();
    this.widgetResized();
  }

  /**
   * Called by base class to let widget register to various app events (like saving, loading, resizing etc)
   */
  registerEvents(): void {
    this.useDefaultServiceSaveChangesHandler("esg", this.provideSaveObject.bind(this));
  }

  /**
   * Called by base class to inform widget that it is initialized
   * Mostly used to define where widget data should be loaded from
   */
  onInitialized(): void {
    if (!this.widgetConfig.hashtagSupported) {
      this.widgetConfig.hashtagSupported = true;
    }
    this.loadReflist('ref_esg_availability');
    this.loadReflist('ref_esg_completeness');
    this.loadReflist('ref_esg_completeness2');
    this.loadReflist('ref_esg_energy_performance_certificate');
    this.loadReflist('ref_esg_energy_source');
    this.loadReflist('ref_esg_green_building_certificate');
    this.loadReflist('ref_esg_location');
    this.loadReflist('ref_esg_status');
    this.loadReflist('ref_esg_yes_no');
    this.loadReflist('ref_esg_yes_no_na');
    this.loadReflist('ref_esg_yes_no_partly_na');

    this.mockupData$ = this.widgetService.loadMockup('esg').pipe(take(1));

    this.widgetData$ = this.mockup ?
      this.mockupData$
      : this.widgetService.load('esg', this.getReport()).pipe();
  }

  /**
   * Called by base class to let widget preprocess loaded data if required
   */
  protected preprocessLoadedData(data) {
    this.widgetData = data;

    //calculate some values (required on data load)
    this.maxValueHeatHotWaterChanged();
    this.maxValueElectricityChanged();
    this.maxValueDemandCertificatePrimaryEnergyChanged();

    return data;//data will be set to widgetData again in base class
  }

  /**
   * Called by base class in save process. Widget should return object to be saved
   */
  async provideSaveObject(): Promise<any> {
    return this.widgetData;
  }

  /**
   * Called by base class to inform widget about save action result
   * @param _success {boolean} The result of save action
   * @param _err {any} The error object, if save action failed
   */
  saveCompleted(_success: boolean, _err: any = null): void {
    //do something if error
  }

  /**
   * Calculates dependent values after maxValueHeatHotWater value changes
   * @param markDirty {boolean} Should we set modely dirty
   */
  maxValueHeatHotWaterChanged(markDirty = false) {
    if (+this.widgetData.maxValueOfTheChartHeatHotWater > 0) {
      this.heatHotWaterPercent = Math.min(+this.widgetData.heatHotWater * 100 / +this.widgetData.maxValueOfTheChartHeatHotWater, 100);
    }

    this.widgetData.finalEnergyTotal = +this.widgetData.electricity + (+this.widgetData.heatHotWater);

    if (markDirty) {
      this.setDirtyFlag(true);
    }
  }

  /**
   * Calculates dependent values after maxValueOfTheChartElectricity value changes
   * @param markDirty {boolean} Should we set modely dirty
   */
  maxValueElectricityChanged(markDirty = false) {
    if (+this.widgetData.maxValueOfTheChartElectricity > 0) {
      this.electricityPercent = Math.min(+this.widgetData.electricity * 100 / +this.widgetData.maxValueOfTheChartElectricity, 100);
    }

    this.widgetData.finalEnergyTotal = +this.widgetData.electricity + (+this.widgetData.heatHotWater);

    if (markDirty) {
      this.setDirtyFlag(true);
    }
  }

  /**
   * Calculates dependent values after maxValueOfTheChartDemandCertificatePrimaryEnergy value changes
   * @param markDirty {boolean} Should we set modely dirty
   */
  maxValueDemandCertificatePrimaryEnergyChanged(markDirty = false) {
    if (+this.widgetData.maxValueOfTheChartDemandCertificatePrimaryEnergy > 0) {
      this.demandCertificatePrimaryEnergyPercent = Math.min(+this.widgetData.demandCertificatePrimaryEnergy * 100 / +this.widgetData.maxValueOfTheChartDemandCertificatePrimaryEnergy, 100);
    }

    if (markDirty) {
      this.setDirtyFlag(true);
    }
  }

  /**
   * If you are using svg-icon in your widgets, these will have to be loaded, which happens independent of the RD loading logic.
   * Especially at print, this can lead to race conditions where the widget data is loaded, but the svg icons aren't.
   * Therefore, they can be pre-loaded in this method, which should be overridden in the widget class
   * @returns
   */
  protected preloadSvgIcons(): Promise<any> {
    return Promise.all([
      this.svgIconService.loadSvg('./assets/images/esgWidget/color_scale_ESG.png')
    ]).then(() => new Promise((resolve) => {
      //Give them time to render
      setTimeout(() => {
        resolve(null);
      }, 100);
    }));
  }
}
